<template>
  <div :class="['image-select', `image-select--${display}`]" role="listbox">
    <button
      v-for="option of options"
      :key="option.id"
      :data-test-id="`ImageSelect_Option-${option.id}`"
      role="option"
      :class="{
        'attest-button': true,
        'attest-button--styleless': true,
        'image-select__option': true,
        'image-select__option--selected': isOptionIdSelected(option.id),
      }"
      :aria-selected="isOptionIdSelected(option.id)"
      @click="() => onClick(option)"
    >
      <button class="image-select__option-expand-button" @click="$emit('viewMedia', option.url)" />
      <figure class="image-select__option-figure">
        <img
          data-test-id="ImageSelect_OptionImage"
          class="image-select__option-image"
          :src="option.url"
          :alt="option.description"
        />
        <figcaption class="image-select__option-caption">
          {{ option.description }}
        </figcaption>
      </figure>
      <div v-if="isTickShown" class="image-select__tick-box" />
    </button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'

export type ImageOption = {
  id: string
  url: string
  description: string | undefined
}

export default defineComponent({
  props: {
    options: {
      type: Array as PropType<readonly ImageOption[]>,
      required: true,
    },
    selectedIds: {
      type: Array as PropType<readonly string[]>,
      required: true,
    },
    display: {
      type: String as PropType<'compact' | 'large'>,
      required: true,
    },
    isTickShown: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    select: (_id: string) => true,
    deselect: (_id: string) => true,
    viewMedia: (_url: string) => true,
  },
  setup(props, { emit }) {
    const isOptionIdSelected = (() => {
      const selectedValueSet = computed(() => new Set(props.selectedIds))
      return (id: string): boolean => selectedValueSet.value.has(id)
    })()

    const onClick = (option: ImageOption): void => {
      if (isOptionIdSelected(option.id)) {
        emit('deselect', option.id)
      } else {
        emit('select', option.id)
      }
    }

    return {
      isOptionIdSelected,
      onClick,
    }
  },
})
</script>
<style scoped lang="postcss">
.image-select {
  display: grid;
  gap: 1rem;

  &--compact {
    grid-template-columns: 1fr 1fr;
  }

  &--large {
    grid-template-columns: 1fr;
  }

  &__option {
    position: relative;
    padding: 4px;
    border: 2px solid var(--image-select-option-border-color, var(--color-blue-grey));
    border-radius: 2px;
    background: white;

    &--selected {
      --image-select-option-border-color: var(--color-selected);
    }
  }

  &__option-expand-button {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 25px;
    height: 25px;
    border: 0;
    border-radius: 50%;
    background-color: var(--color-selected);
    background-image: url('../../../interview/assets/expand-white.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: auto 11px;
    cursor: pointer;
  }

  &__option--selected &__tick-box {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    display: flex;
    width: 1.5rem;
    height: 1.5rem;
    justify-content: center;
    padding-top: 0.2rem;
    border: 1px solid var(--color-selected);
    border-radius: 64px;
    background: var(--color-selected);

    &::after {
      width: 0.3rem;
      height: 0.6rem;
      border-right: 2px white solid;
      border-bottom: 2px white solid;
      content: '';
      rotate: 45deg;
    }
  }

  &__option-figure {
    display: flex;
    height: 100%;
    flex-direction: column;
    justify-content: flex-end;
    margin: 0;
  }

  &__option-image {
    max-width: 100%;
    margin-top: auto;
    margin-bottom: auto;
  }

  &--compact &__option-image {
    max-height: 204px;
    align-self: center;
  }

  &__option-caption:not(:empty) {
    padding: 0.5rem;
    font-weight: 400;
  }
}
</style>
